import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useAuthStore } from '@/core/auth/auth-store'
import { getProfile, mockMatches, mockPastMatches, type MockMatch } from '@/shared/mock/data'
import { matchInvitesRepository } from './api/match-invites-repository'
import type { MatchInviteDoc } from '@/core/models/match-invite'
import type { MatchView } from './use-matches'

// `uid!` below is safe by the query's own gate: every one of these hooks sets
// `enabled: uid !== null` (or `!AUTH_BYPASS && uid !== null`), so TanStack
// Query never invokes the fn while uid is null. Asserting is preferable to a
// `?? ''` fallback, which would silently issue a query for the empty-string
// user and return an honest-looking empty result instead of not running.


// Match invitations (spec/02): organizer picks contacts → invite docs →
// push CF → the invitee sees the strip on Partidos. Deterministic ids make
// re-invites idempotent; accepting rides the normal join (marked
// opportunistically — the join itself is the source of truth).

/**
 * Bypass fixtures are DERIVED FROM the mock match they link to — never
 * hand-typed alongside it.
 *
 * They used to be typed separately, and drifted: the pending invite displayed
 * "Ana Pérez · Club Pádel Zaidín · Mañana 19:00" while `matchId` pointed at
 * `p0`, which is Maristas Padel, nine days PAST, and organized by you. Tapping
 * "Ver" opened a match with nothing to do with the invite. A persona walk found
 * it on 2026-08-08; every design review before that had read the same lie.
 *
 * No player could hit it — production denormalizes place/day/time from the
 * match inside `sendAll`, so a real invite cannot disagree with itself. But the
 * stub is what EVERY design review and persona walk sees, so a wrong fixture
 * corrupts every future review. Same class as the mock date rot that showed
 * "Hoy · sáb, 4 jul" in August.
 *
 * Deriving mirrors production's own shape: one fact, one store.
 */
function inviteFromMock(
  match: MockMatch,
  opts: { status: MatchInviteDoc['status'] },
): MatchInviteDoc {
  return {
    id: `${match.id}_me`,
    matchId: match.id,
    fromUid: `mock-${match.organizer.split(' ')[0]?.toLowerCase() ?? 'org'}`,
    // The inviter IS the organizer — the rules require it (create checks the
    // sender is the match organizer), so a fixture must not imply otherwise.
    fromName: match.organizer,
    toUid: 'me',
    status: opts.status,
    place: match.place,
    day: match.dayLabel,
    time: match.timeLabel,
    createdAt: null,
    updatedAt: null,
  }
}

/** Pending: a FUTURE match the player is not already in. */
// `[1]!` / `[1]!` — both arrays are module-level literals with fixed members
// (see shared/mock/data.ts); index 1 is m2 and p1 respectively.
const BYPASS_INVITE: MatchInviteDoc = inviteFromMock(mockMatches[1]!, { status: 'pending' })
/** History: a past match, already accepted. */
const BYPASS_INVITE_PAST: MatchInviteDoc = inviteFromMock(mockPastMatches[1]!, {
  status: 'accepted',
})

/**
 * Dismissals/acceptances made during a bypass walk, so the UI actually
 * responds. "Descartar invitación" used to be a dead tap: the mutation
 * resolved without touching anything and the list was a constant, so the
 * invalidated query refetched identical data and nothing moved.
 */
const bypassResponded = new Map<string, MatchInviteDoc['status']>()

function applyBypassResponses(list: MatchInviteDoc[]): MatchInviteDoc[] {
  return list.map((inv) => {
    const s = bypassResponded.get(inv.id)
    return s ? { ...inv, status: s } : inv
  })
}

/** My pending invitations — the strip above the feed. */
export function usePendingInvites() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['match-invites', 'pending', uid],
    queryFn: () =>
      AUTH_BYPASS
        ? Promise.resolve(
            applyBypassResponses([BYPASS_INVITE]).filter((i) => i.status === 'pending'),
          )
        : matchInvitesRepository.listPendingFor(uid!),
    enabled: AUTH_BYPASS || uid !== null,
    staleTime: 30_000,
  })
}

/** Full invitation history — the Actividad screen (newest first). */
export function useMyActivity() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['match-invites', 'all', uid],
    queryFn: async () => {
      const docs = AUTH_BYPASS
        // Actividad shows HISTORY, so a dismissed invite stays visible here —
        // it just reads "Rechazada" instead of pending. That is the whole point
        // of the screen: nothing silently disappears from your record.
        ? applyBypassResponses([BYPASS_INVITE, BYPASS_INVITE_PAST])
        : await matchInvitesRepository.listAllFor(uid!)
      return [...docs].sort(
        (a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0),
      )
    },
    enabled: AUTH_BYPASS || uid !== null,
    staleTime: 30_000,
  })
}

/** Uids I already invited to this match (picker "Invitado ✓" state). */
export function useSentInvites(matchId: string | undefined, isOrganizer: boolean) {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['match-invites', 'sent', matchId, uid],
    queryFn: () => // `matchId!` alongside `uid!` — `enabled` requires `!!matchId` too.
      matchInvitesRepository.listSentForMatch(uid!, matchId!),
    enabled: !AUTH_BYPASS && uid !== null && !!matchId && isOrganizer,
    staleTime: 30_000,
  })
}

export function useSendInvites() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ match, toUids }: { match: MatchView; toUids: string[] }) => {
      if (AUTH_BYPASS) return Promise.resolve()
      const fromName = getProfile().name || 'Un jugador'
      return matchInvitesRepository.sendAll(match, uid!, fromName, toUids)
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['match-invites'] }),
  })
}

export function useRespondInvite() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ matchId, status }: { matchId: string; status: 'accepted' | 'declined' }) => {
      if (AUTH_BYPASS) {
        // Record it, so the row actually leaves the list. Resolving without
        // recording made "Descartar invitación" a dead tap: the invalidated
        // query refetched an identical constant and nothing on screen moved.
        bypassResponded.set(`${matchId}_me`, status)
        return Promise.resolve()
      }
      return matchInvitesRepository.respond(matchId, uid!, status)
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['match-invites'] }),
  })
}

/** Joining a match I was invited to marks the invite accepted — best-effort
 *  (the membership transaction is the truth; a failure here is invisible). */
export function markInviteAccepted(matchId: string, uid: string | null): void {
  if (AUTH_BYPASS || !uid) return
  matchInvitesRepository.respond(matchId, uid, 'accepted').catch(() => {})
}
